/** Pure Unicode style tables and transforms for the Fancy Font Generator (no images, no fonts). */

export type ZalgoIntensity = 1 | 2 | 3;
export const DEFAULT_OPTIONS: StyleOptions = { zalgoIntensity: 2 };

export const BOX_HINT_ENCLOSED =
  "Squared and filled-bubble letters are newer Unicode symbols and may show as empty boxes on older Android and Windows, and in some apps' name fields.";
export const BOX_HINT_CIRCLED =
  "Circled letters may show as boxes in some fonts and apps; check the result where you plan to paste it.";
export const ZALGO_HINT =
  "Stacked marks can be cut off, overlap the lines around them, or be stripped by some sites and name fields.";

// Combining marks, grouped by where they sit on the letter.
const MARKS_UP = ["\u030d", "\u030e", "\u0304", "\u0305", "\u033f", "\u0311", "\u0306", "\u0310", "\u0352", "\u0357", "\u0351", "\u0307", "\u0308", "\u030a", "\u0342", "\u0343", "\u0344", "\u034a", "\u034b", "\u034c", "\u0303", "\u0302", "\u030c", "\u0350", "\u0300", "\u0301", "\u030b", "\u030f", "\u0312", "\u0313", "\u0314", "\u033d", "\u0309", "\u0363", "\u0364", "\u0365", "\u0366", "\u0367", "\u0368", "\u0369", "\u036a", "\u036b", "\u036c", "\u036d", "\u036e", "\u036f", "\u033e", "\u035b"];
const MARKS_MID = ["\u0315", "\u031b", "\u0340", "\u0341", "\u0358", "\u0321", "\u0322", "\u0327", "\u0328", "\u0334", "\u0335", "\u0336", "\u034f", "\u035c", "\u035d", "\u035e", "\u035f", "\u0360", "\u0362", "\u0338", "\u0337", "\u0361", "\u0489"];
const MARKS_DOWN = ["\u0316", "\u0317", "\u0318", "\u0319", "\u031c", "\u031d", "\u031e", "\u031f", "\u0320", "\u0324", "\u0325", "\u0326", "\u0329", "\u032a", "\u032b", "\u032c", "\u032d", "\u032e", "\u032f", "\u0330", "\u0331", "\u0332", "\u0333", "\u0339", "\u033a", "\u033b", "\u033c", "\u0345", "\u0347", "\u0348", "\u0349", "\u034d", "\u034e", "\u0353", "\u0354", "\u0355", "\u0356", "\u0359", "\u035a", "\u0323"];

/** How many marks each letter gets above / through / below, per intensity. */
export const ZALGO_COUNTS: Record<ZalgoIntensity, { up: number; mid: number; down: number }> = {
  1: { up: 1, mid: 0, down: 1 },
  2: { up: 3, mid: 1, down: 3 },
  3: { up: 6, mid: 2, down: 6 },
};

const isCombining = (c: string) => /[\u0300-\u036f\u0489]/.test(c);

/** Small seeded generator so the preview and the copied text are the same string on every render. */
function seeded(text: string) {
  let h = 2166136261;
  for (const ch of text) h = Math.imul(h ^ ch.codePointAt(0)!, 16777619) >>> 0;
  return () => {
    h = (Math.imul(h, 1664525) + 1013904223) >>> 0;
    return h / 4294967296;
  };
}

/** Adds stacked combining marks to every letter. Same text and intensity always give the same result; intensity 1 to 3 adds more marks. */
export function zalgo(text: string, intensity: ZalgoIntensity = 2): string {
  const rand = seeded(text + ":" + intensity);
  const n = ZALGO_COUNTS[intensity] ?? ZALGO_COUNTS[2];
  const pick = (set: string[], count: number) => Array.from({ length: count }, () => set[Math.floor(rand() * set.length)]).join("");
  return Array.from(text)
    .map((c) => (/\s/.test(c) || isCombining(c) ? c : c + pick(MARKS_UP, n.up) + pick(MARKS_MID, n.mid) + pick(MARKS_DOWN, n.down)))
    .join("");
}

const GLITCH_OVER = ["\u0335", "\u0337", "\u0338", "\u0336"];
const GLITCH_UNDER = ["\u0324", "\u0330", "\u0353", "\u0325"];
const GLITCH_UP = ["\u0346", "\u0357", "\u0352", "\u0351"];

/** Lighter, steadier than zalgo: a cut-through bar plus one mark above and below, cycling by letter position so it reads as a screen glitch. */
export function glitch(text: string): string {
  let i = 0;
  return Array.from(text)
    .map((c) => {
      if (/\s/.test(c) || isCombining(c)) return c;
      const k = i++;
      return c + GLITCH_OVER[k % GLITCH_OVER.length] + GLITCH_UP[(k + 1) % GLITCH_UP.length] + GLITCH_UNDER[(k + 2) % GLITCH_UNDER.length];
    })
    .join("");
}

const UPPER = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const LOWER = "abcdefghijklmnopqrstuvwxyz";
const DIGITS = "0123456789";

/** Builds a char->char map from a contiguous Unicode block, with optional exceptions for irregular codepoints (common in math alphanumeric blocks). */
function buildMap(upperStart: number, lowerStart: number, digitStart: number | null, exceptions: Record<string, string> = {}) {
  const map: Record<string, string> = {};
  Array.from(UPPER).forEach((ch, i) => (map[ch] = String.fromCodePoint(upperStart + i)));
  Array.from(LOWER).forEach((ch, i) => (map[ch] = String.fromCodePoint(lowerStart + i)));
  if (digitStart !== null) {
    Array.from(DIGITS).forEach((ch, i) => (map[ch] = String.fromCodePoint(digitStart + i)));
  }
  return { ...map, ...exceptions };
}

const FLIP: Record<string, string> = Object.fromEntries(
  Array.from("abcdefghijklmnopqrstuvwxyz").map((c, i) => [c, Array.from("ɐqɔpǝɟƃɥᴉɾʞlɯuodbɹsʇnʌʍxʎz")[i]]).concat(
    Array.from("0123456789").map((c, i) => [c, Array.from("0⇂ᘔƐ߈ϛ9ㄥ86")[i]]),
    [["?", "¿"], ["!", "¡"], [".", "˙"], [",", "'"], ["'", ","], ["(", ")"], [")", "("], ["[", "]"], ["]", "["], ["<", ">"], [">", "<"], ["&", "⅋"], ["_", "‾"]],
  ),
);

export type StyleOptions = { zalgoIntensity: ZalgoIntensity };
export type FancyStyle = {
  label: string;
  /** Stable id for styles that need extra controls (the zalgo intensity picker). */
  id?: "zalgo";
  /** One line shown under the row when the characters are known to drop out on some platforms. */
  hint?: string;
  map?: Record<string, string>;
  transform?: (s: string, o: StyleOptions) => string;
};

export const STYLES: FancyStyle[] = [
  { label: "Bold", map: buildMap(0x1d400, 0x1d41a, 0x1d7ce) },
  {
    label: "Italic",
    map: buildMap(0x1d434, 0x1d44e, null, { h: "\u210e" }),
  },
  { label: "Bold Italic", map: buildMap(0x1d468, 0x1d482, null) },
  {
    label: "Script",
    map: buildMap(0x1d49c, 0x1d4b6, null, {
      B: "\u212c", E: "\u2130", F: "\u2131", H: "\u210b", I: "\u2110", L: "\u2112", M: "\u2133", R: "\u211b",
      e: "\u212f", g: "\u210a", o: "\u2134",
    }),
  },
  {
    label: "Fraktur",
    map: buildMap(0x1d504, 0x1d51e, null, {
      C: "\u212d", H: "\u210c", I: "\u2111", R: "\u211c", Z: "\u2128",
    }),
  },
  {
    label: "Double-Struck",
    map: buildMap(0x1d538, 0x1d552, 0x1d7d8, {
      C: "\u2102", H: "\u210d", N: "\u2115", P: "\u2119", Q: "\u211a", R: "\u211d", Z: "\u2124",
    }),
  },
  { label: "Sans-Serif", map: buildMap(0x1d5a0, 0x1d5ba, 0x1d7e2) },
  { label: "Sans Bold", map: buildMap(0x1d5d4, 0x1d5ee, 0x1d7ec) },
  { label: "Monospace", map: buildMap(0x1d670, 0x1d68a, 0x1d7f6) },
  { label: "Fullwidth", map: buildMap(0xff21, 0xff41, 0xff10) },
  {
    label: "Small Caps",
    map: (() => {
      const smallCaps = "ᴀʙᴄᴅᴇꜰɢʜɪᴊᴋʟᴍɴᴏᴘꞯʀꜱᴛᴜᴠᴡxʏᴢ";
      const map: Record<string, string> = {};
      Array.from(UPPER).forEach((ch, i) => (map[ch] = smallCaps[i]));
      Array.from(LOWER).forEach((ch, i) => (map[ch] = smallCaps[i]));
      return map;
    })(),
  },
  { label: "Bold Script", map: buildMap(0x1d4d0, 0x1d4ea, null) },
  { label: "Bold Fraktur", map: buildMap(0x1d56c, 0x1d586, null) },
  { label: "Italic Sans", map: buildMap(0x1d608, 0x1d622, null) },
  { label: "Bold Italic Sans", map: buildMap(0x1d63c, 0x1d656, null) },
  { label: "Circled", hint: BOX_HINT_CIRCLED, map: buildMap(0x24b6, 0x24d0, null, { ...Object.fromEntries(Array.from("123456789").map((d, i) => [d, String.fromCodePoint(0x2460 + i)])), "0": "\u24ea" }) },
  { label: "Bubble (filled)", hint: BOX_HINT_ENCLOSED, map: buildMap(0x1f150, 0x1f150, null, { ...Object.fromEntries(Array.from("123456789").map((d, i) => [d, String.fromCodePoint(0x2776 + i)])), "0": "\u24ff" }) },
  { label: "Squared", hint: BOX_HINT_ENCLOSED, map: buildMap(0x1f130, 0x1f130, null) },
  {
    label: "Upside Down",
    transform: s => Array.from(s).map(c => FLIP[c] ?? FLIP[c.toLowerCase()] ?? c).reverse().join(""),
  },
  {
    label: "Strikethrough",
    transform: s => Array.from(s).map(c => (/\s/.test(c) ? c : c + "\u0336")).join(""),
  },
  {
    label: "Underline",
    transform: s => Array.from(s).map(c => (/\s/.test(c) ? c : c + "\u0332")).join(""),
  },
  { label: "Zalgo", id: "zalgo", hint: ZALGO_HINT, transform: (s, o) => zalgo(s, o.zalgoIntensity) },
  { label: "Glitch", hint: ZALGO_HINT, transform: glitch },
];

export function convert(text: string, style: FancyStyle, o: StyleOptions = DEFAULT_OPTIONS): string {
  if (style.transform) return style.transform(text, o);
  if (!style.map) return text;
  // Decompose accents (é -> e + ́) so the base letter is styled and the accent kept.
  return Array.from(text.normalize("NFD")).map(ch => style.map![ch] ?? ch).join("").normalize("NFC");
}

/** Decorative borders applied on top of whichever style is selected --
 * the common "wrap your styled text in symbols" pattern people actually
 * use these tools for on social bios, Discord names, etc. "None" is the
 * default so nothing changes for anyone who doesn't want this. */
export const DECORATIONS: { label: string; name: string; wrap: (s: string) => string }[] = [
  { label: "None", name: "None", wrap: s => s },
  { label: "✨ Sparkle", name: "Sparkle", wrap: s => `✨ ${s} ✨` },
  { label: "☆ Star", name: "Star", wrap: s => `☆ ${s} ☆` },
  { label: "『 』 Brackets", name: "Bracket", wrap: s => `『 ${s} 』` },
  { label: "⋆｡° Dreamy", name: "Dreamy", wrap: s => `⋆｡°✩ ${s} ✩°｡⋆` },
  { label: "🔥 Fire", name: "Fire", wrap: s => `🔥 ${s} 🔥` },
  { label: "🌸 Blossom", name: "Blossom", wrap: s => `🌸 ${s} 🌸` },
  { label: "▸ Arrows", name: "Arrow", wrap: s => `»»— ${s} —««` },
];

